import React, { useEffect, useState } from 'react';
import { Map, Building2, TrendingUp, Loader2, MapPin, Globe } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { IntelligenceLayout, fetchWithAuth, MetricCard, useCompanyFilters, CompanyFilterBar } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_ABBREVS = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
  'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
};

const STATE_COLORS = [
  '#0A1F44', '#1a3a6e', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd',
  '#7c3aed', '#a855f7', '#c084fc', '#d97706', '#f59e0b', '#fbbf24',
  '#059669', '#10b981', '#34d399', '#dc2626', '#ef4444', '#f87171',
];

// Squarified treemap layout - computes {x, y, w, h} for each item
const computeTreemapLayout = (items, totalW, totalH) => {
  if (!items.length) return [];
  const totalValue = items.reduce((s, it) => s + it.size, 0);
  const rects = [];
  let remaining = [...items];
  let x = 0, y = 0, w = totalW, h = totalH;

  while (remaining.length > 0) {
    const isWide = w >= h;
    const side = isWide ? h : w;
    let row = [remaining[0]];
    let rowSum = remaining[0].size;
    let bestRatio = Infinity;

    for (let i = 1; i < remaining.length; i++) {
      const candidate = [...row, remaining[i]];
      const candidateSum = rowSum + remaining[i].size;
      const rowFraction = candidateSum / totalValue;
      const rowSide = (isWide ? w : h) * rowFraction;
      let worstRatio = 0;
      for (const c of candidate) {
        const cellSide = (c.size / candidateSum) * side;
        const ratio = Math.max(rowSide / cellSide, cellSide / rowSide);
        worstRatio = Math.max(worstRatio, ratio);
      }
      const currentFraction = rowSum / totalValue;
      const currentSide = (isWide ? w : h) * currentFraction;
      let currentWorst = 0;
      for (const c of row) {
        const cellSide = (c.size / rowSum) * side;
        const ratio = Math.max(currentSide / cellSide, cellSide / currentSide);
        currentWorst = Math.max(currentWorst, ratio);
      }
      if (worstRatio <= currentWorst) {
        row.push(remaining[i]);
        rowSum += remaining[i].size;
      } else break;
    }

    const rowFraction = rowSum / totalValue;
    const rowThickness = (isWide ? w : h) * rowFraction;
    let offset = 0;
    for (const item of row) {
      const cellLength = (item.size / rowSum) * side;
      rects.push({
        ...item,
        x: isWide ? x : x + offset,
        y: isWide ? y + offset : y,
        w: isWide ? rowThickness : cellLength,
        h: isWide ? cellLength : rowThickness,
      });
      offset += cellLength;
    }

    if (isWide) { x += rowThickness; w -= rowThickness; }
    else { y += rowThickness; h -= rowThickness; }
    remaining = remaining.slice(row.length);
    totalValue > 0 && remaining.length > 0 && (() => {})();
  }
  return rects;
};

const CSSTreemap = ({ data }) => {
  const containerRef = React.useRef(null);
  const [dims, setDims] = useState({ w: 700, h: 400 });

  useEffect(() => {
    if (containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      if (width > 0) setDims({ w: width, h: 400 });
    }
  }, []);

  const layout = computeTreemapLayout(data, dims.w, dims.h);

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: 400 }} data-testid="treemap-container">
      {layout.map((cell, i) => (
        <div key={i} className="absolute flex flex-col items-center justify-center overflow-hidden transition-opacity hover:opacity-90"
          data-testid={`treemap-cell-${cell.name}`}
          style={{
            left: cell.x, top: cell.y, width: cell.w, height: cell.h,
            backgroundColor: cell.fill,
            borderRadius: cell.w > 40 ? 6 : 3,
            border: '2.5px solid #fff',
          }}
          title={`${cell.state}: ${cell.size?.toLocaleString()} jobs, ${cell.companies} companies`}
        >
          {cell.w > 40 && cell.h > 28 && (
            <>
              <span className="text-white font-semibold leading-none" style={{ fontSize: cell.w > 90 ? 18 : cell.w > 55 ? 15 : 12 }}>{cell.name}</span>
              <span className="text-white/80 leading-none mt-1" style={{ fontSize: cell.w > 90 ? 14 : 11 }}>{cell.size?.toLocaleString()}</span>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#0A1F44] text-white px-3 py-2 rounded-lg text-xs shadow-xl">
      <p className="font-medium">{d.state || d.city}</p>
      <p>{d.total_jobs?.toLocaleString()} jobs</p>
      {d.companies_count && <p>{d.companies_count} companies</p>}
    </div>
  );
};

const GeoHeatmap = ({ embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('states');
  const { excludeStaffing, setExcludeStaffing, excludeRetail, setExcludeRetail, filterParams } = useCompanyFilters();

  useEffect(() => {
    setLoading(true);
    fetchWithAuth(`${API_URL}/api/intelligence/geographic-heatmap?limit=50${filterParams}`)
      .then(d => setData(d)).catch(() => {}).finally(() => setLoading(false));
  }, [excludeStaffing, excludeRetail]);

  const states = (data?.states || []).map(s => ({ ...s, abbrev: STATE_ABBREVS[s.state] || s.state }));
  const cities = data?.cities || [];
  const topCity = cities[0];
  const topState = states[0];

  // Treemap data for state visualization
  const treemapData = states.slice(0, 20).map((s, i) => ({
    name: s.abbrev,
    size: s.total_jobs,
    fill: STATE_COLORS[i % STATE_COLORS.length],
    state: s.state,
    companies: s.companies_count,
  }));

  return (
    <IntelligenceLayout title="Geo Heatmap" embedded={embedded}>
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <Map className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="geo-heatmap-title">Geographic Hiring Heatmap</h1>
            <p className="text-sm text-gray-500">Emerging hiring hotspots across the United States</p>
          </div>
        </div>
      </header>

      <main className="p-8 space-y-6">
        <CompanyFilterBar excludeStaffing={excludeStaffing} setExcludeStaffing={setExcludeStaffing}
          excludeRetail={excludeRetail} setExcludeRetail={setExcludeRetail} />
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-2 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /> Mapping hiring hotspots...</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="geo-metrics">
              <MetricCard label="Total Jobs" value={(data?.total_jobs || 0).toLocaleString()} icon={Building2} />
              <MetricCard label="Active States" value={data?.total_states || 0} icon={Globe} />
              <MetricCard label="Active Cities" value={data?.total_cities || 0} icon={MapPin} />
              <MetricCard label="Top State" value={topState?.abbrev || '—'} icon={TrendingUp} subtext={`${topState?.total_jobs?.toLocaleString() || 0} jobs`} />
            </div>

            {/* View toggle */}
            <div className="flex items-center gap-3" data-testid="geo-view-toggle">
              <Globe className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600 font-medium">View:</span>
              {['states', 'cities'].map(v => (
                <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${view === v ? 'bg-[#0A1F44] text-white shadow-md' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                  {v === 'states' ? 'By State' : 'By City'}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* State treemap */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="state-treemap">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">Hiring Volume by State</h3>
                <CSSTreemap data={treemapData} />
              </div>

              {/* Bar chart */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm" data-testid="state-bar-chart">
                <h3 className="text-sm font-semibold text-[#0A1F44] mb-4">
                  {view === 'states' ? 'Top Hiring States' : 'Top Hiring Cities'}
                </h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={view === 'states' ? states.slice(0, 15).map(s => ({ name: s.abbrev, jobs: s.total_jobs, companies: s.companies_count }))
                      : cities.slice(0, 15).map(c => ({ name: c.city.length > 12 ? c.city.slice(0, 10) + '..' : c.city, jobs: c.total_jobs, companies: c.companies_count }))}
                    layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" width={60} tick={{ fontSize: 10 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="jobs" radius={[0, 4, 4, 0]}>
                      {(view === 'states' ? states : cities).slice(0, 15).map((_, i) => (
                        <Cell key={i} fill={STATE_COLORS[i % STATE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* State/City detail table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="geo-detail-table">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <h3 className="text-sm font-semibold text-[#0A1F44]">{view === 'states' ? 'State Details' : 'City Details'}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500 uppercase border-b border-gray-100 bg-gray-50/30">
                      <th className="text-left px-4 py-3 w-8">#</th>
                      <th className="text-left px-4 py-3">{view === 'states' ? 'State' : 'City'}</th>
                      {view === 'cities' && <th className="text-left px-4 py-3">State</th>}
                      <th className="text-right px-4 py-3">Jobs</th>
                      <th className="text-right px-4 py-3">Companies</th>
                      {view === 'states' && <th className="text-right px-4 py-3">Cities</th>}
                      <th className="text-left px-4 py-3">Top Industries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(view === 'states' ? states : cities).map((item, i) => (
                      <tr key={i} className="border-t border-gray-100 hover:bg-[#f8f6f0] transition-colors" data-testid={`geo-row-${i}`}>
                        <td className="px-4 py-3 text-xs text-gray-400">{i + 1}</td>
                        <td className="px-4 py-3 font-semibold text-[#0A1F44]">{view === 'states' ? item.state : item.city}</td>
                        {view === 'cities' && <td className="px-4 py-3 text-gray-500">{item.state}</td>}
                        <td className="px-4 py-3 text-right font-bold">{item.total_jobs.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{item.companies_count}</td>
                        {view === 'states' && <td className="px-4 py-3 text-right text-gray-600">{item.cities_count}</td>}
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(item.top_industries || []).map((ind, j) => (
                              <span key={j} className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-200">{ind}</span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </IntelligenceLayout>
  );
};

export default GeoHeatmap;
